import { Box, Button, Card, Grid, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getResidentDetails, updateTransportRequest, updateResidentDetails } from '../../../../../api/transports';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import ConfirmDialogModal from '../../../../../reusable-components/confirm-dialog/ConfirmDialogModal';
import { useScopeCheck } from '../../../../../reusable-components/scopes';
import {
  FIELDS,
  FIELDS_ALWAYS_REQUIRED,
  PICKUP_TYPE_DOCTOR,
  PICKUP_TYPE_HOME,
  TRANSPORT_ADMIN,
  TRANSPORT_COORDINATOR,
  TRANSPORT_TIME_ZONE_ID,
} from '../../constants';
import { REQUEST_STATUS, REQUEST_STATUS_NAMES } from '../../statusEnums';
import { formReducer, initialFormState } from '../formReducer';
import { formatDateTimeWithoutTimezone } from '../../../../../utils/formatTime';
import { buildTransportRequestPayload, mapRequestToFormState } from '../utils';
import ActionBar from './ActionBar';
import AdditionalDetailsTab from './AdditionalDetailsTab';
import ResidentDetailsTab from './ResidentDetailsTab';
import ResidentHeaderSection from './ResidentHeaderSection';
import TransportDetailsTab from './TransportDetailsTab';
import Alert from '../../Shared/Alert';

RequestDetailsForm.propTypes = {
  request: PropTypes.object.isRequired,
  setRequest: PropTypes.func.isRequired,
};

const transportDetailsTab = 'transportDetails';
const residentDetailsTab = 'residentDetails';
const additionalDetailsTab = 'additionalDetails';
const residentEditableFields = ['unit', 'room', 'bed'];

const tabStyle = {
  fontWeight: 600,
  fontSize: '18px',
  textTransform: 'none',
  color: 'text.secondary',
  '&.Mui-disabled': {
    pointerEvents: 'auto',
    cursor: 'not-allowed',
  },
  '&.Mui-selected': {
    color: 'primary.main',
    borderBottom: '2px solid',
    borderColor: 'primary.main',
  },
};

export default function RequestDetailsForm({ request, setRequest }) {
  const getRecurrenceSnapshot = (state) => ({
    RecurrenceType: state?.RecurrenceType,
    RecurrenceFrequency: String(state?.RecurrenceFrequency ?? ''),
    RecurrenceEndDate: state?.RecurrenceEndDate ? formatDateTimeWithoutTimezone(state.RecurrenceEndDate).slice(0, 10) : '',
    RecurrenceDaysOfWeek: JSON.stringify(state?.RecurrenceDaysOfWeek ?? []),
    RecurrenceDayOfMonth: String(state?.RecurrenceDayOfMonth ?? ''),
    RecurrenceWeekOfMonth: String(state?.RecurrenceWeekOfMonth ?? ''),
  });

  const navigate = useNavigate();
  const { date } = useParams();
  const mapToFormState = (req) => mapRequestToFormState(req, initialFormState, FIELDS, PICKUP_TYPE_HOME, PICKUP_TYPE_DOCTOR);
  const [formState, formDispatch] = useReducer(formReducer, mapToFormState(request));
  const setFields = (fields) => formDispatch({ type: 'setFields', fields });
  const [selectedTab, setSelectedTab] = useState('transportDetails');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [updatedFutureEvents, setUpdatedFutureEvents] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(null);
  const [isAllowedPickupDate, setIsAllowedPickupDate] = useState(true);
  const [isAllowedAppointmentDate, setIsAllowedAppointmentDate] = useState(true);
  const wasRecurring = useRef(request?.isRecurring ?? false);
  const recurrenceBaseline = useRef(getRecurrenceSnapshot(formState));
  const [residentDetails, setResidentDetails] = useState(null);
  const [residentDraft, setResidentDraft] = useState(null);
  const [residentDetailsError, setResidentDetailsError] = useState('');
  const transportDetailsTabIsActive = selectedTab === transportDetailsTab;
  const residentDetailsTabIsActive = selectedTab === residentDetailsTab;
  const allowEdit = useScopeCheck([TRANSPORT_ADMIN, TRANSPORT_COORDINATOR], false, true);
  const canEdit =
    allowEdit &&
    request.requestStatusId !== REQUEST_STATUS.CANCELLED &&
    request.requestStatusId !== REQUEST_STATUS.COMPLETE;
  const readOnlyMessage = allowEdit
    ? `${REQUEST_STATUS_NAMES[request.requestStatusId]} requests cannot be edited`
    : 'You do not have permission to edit this request';

  const baseFormState = useMemo(() => mapToFormState(request), [request]);
  const transportFormChanged = useMemo(
    () =>
      Object.keys({ ...baseFormState, ...formState }).some(
        (key) => JSON.stringify(formState[key] ?? null) !== JSON.stringify(baseFormState[key] ?? null)
      ),
    [formState, baseFormState]
  );
  const residentFormChanged = Boolean(
    residentDraft &&
      residentEditableFields.some((field) => (residentDraft[field] || '') !== (residentDetails?.[field] || ''))
  );
  const hasUnsavedChanges = transportFormChanged || residentFormChanged;

  const handleCancelEdit = () => {
    formDispatch({ type: 'setFields', fields: baseFormState });
    setSubmitError('');
    setShowConfirmationPopup(null);
    setResidentDraft(null);
    recurrenceBaseline.current = getRecurrenceSnapshot(baseFormState);
  };

  // Syncs the request prop and the editable form with the request returned from the server
  const syncStateFromResponse = (data) => {
    setRequest(data);
    formDispatch({ type: 'setFields', fields: mapToFormState(data) });
  };

  const resetSubmitState = () => {
    setSubmitLoading(true);
    setSubmitError('');
    closeSuccessAlert();
  };

  const handleResidentSubmit = async () => {
    if (!residentDraft?.id) {
      setSubmitError('Unable to save resident changes');
      return;
    }

    const residentPayload = {
      id: residentDraft.id,
      unit: (residentDraft.unit || '').trim() || null,
      room: (residentDraft.room || '').trim() || null,
      bed: (residentDraft.bed || '').trim() || null,
    };

    resetSubmitState();
    try {
      const response = await updateResidentDetails(residentPayload);
      setResidentDetails(response?.data);
      setResidentDraft(null);
      setShowSaveSuccess(true);
    } catch (err) {
      setSubmitError(err.message || 'Unable to save changes');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResidentFieldChange = (field) => (event) => {
    const { value } = event.target;
    setResidentDraft((previous) => ({
      ...(previous || residentDetails),
      [field]: value,
    }));
  };

  const handleSubmit = async (individualOccurrenceEdit = false) => {
    if (formState.IsRecurring && !formState.AppointmentDateTime) {
      setSubmitError('Recurring transports require an appointment date and time');
      return;
    }

    const payload = buildTransportRequestPayload(formState, FIELDS);
    if (payload.IsRecurring) {
      payload[FIELDS.TIME_ZONE_ID] = TRANSPORT_TIME_ZONE_ID;
    }

    resetSubmitState();
    try {
      const response = await updateTransportRequest(payload, individualOccurrenceEdit);
      const { data } = response;
      syncStateFromResponse(data);
      wasRecurring.current = data?.isRecurring ?? false;
      setShowSaveSuccess(true);
      setUpdatedFutureEvents(!individualOccurrenceEdit && formState.IsRecurring);
      recurrenceBaseline.current = getRecurrenceSnapshot(formState);

      const requestId = data?.id;
      if (requestId) {
        navigate(`/dashboard/transports/request-details/${requestId}${date ? `/${date}` : ''}`, { state: { request: data } });
      }
    } catch (err) {
      setSubmitError(err.message || 'Unable to save changes');
    } finally {
      setSubmitLoading(false);
    }
  };

  const isFormValid = useMemo(() => {
    // Always-required: null, undefined, or '' all count as missing
    if (FIELDS_ALWAYS_REQUIRED.some((f) => formState[f] === null || formState[f] === undefined || formState[f] === ''))
      return false;

    // Conditional requirements
    if (!isAllowedAppointmentDate || !isAllowedPickupDate) return false;
    if (!formState.AppointmentDateTime && !formState.SeenByDate) return false;
    if (formState.PickupLocationType === PICKUP_TYPE_DOCTOR && !formState.PickupDoctorId) return false;
    if (formState.DropoffLocationType === PICKUP_TYPE_DOCTOR && !formState.DropoffDoctorId) return false;

    return true;
  }, [formState, isAllowedAppointmentDate, isAllowedPickupDate]);

  // Helper to check if recurrence rules have changed
  const recurrenceRulesChanged = useMemo(() => {
    if (!hasUnsavedChanges || !formState.IsRecurring || !wasRecurring.current) return false;
    const orig = recurrenceBaseline.current;
    if (!orig) return false;
    const current = getRecurrenceSnapshot(formState);
    return (
      orig.RecurrenceType !== current.RecurrenceType ||
      orig.RecurrenceFrequency !== current.RecurrenceFrequency ||
      orig.RecurrenceEndDate !== current.RecurrenceEndDate ||
      orig.RecurrenceDaysOfWeek !== current.RecurrenceDaysOfWeek ||
      orig.RecurrenceDayOfMonth !== current.RecurrenceDayOfMonth ||
      orig.RecurrenceWeekOfMonth !== current.RecurrenceWeekOfMonth
    );
  }, [hasUnsavedChanges, formState]);

  const closeSuccessAlert = () => {
    setShowSaveSuccess(false);
    setUpdatedFutureEvents(false);
  };

  useEffect(() => {
    formDispatch({ type: 'setFields', fields: baseFormState });
  }, [baseFormState]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      closeSuccessAlert();
    }
  }, [hasUnsavedChanges]);

  useEffect(() => {
    // For all transport types other than group trip, only one resident is allowed so we only consider the first resident in the list
    // Group trip multiple residents functionality planned for future implementation
    const residentId = request.residents?.[0]?.residentId;
    if (!residentId) {
      setResidentDetails(null);
      setResidentDraft(null);
      setResidentDetailsError('');
      return;
    }

    setResidentDetailsError('');
    getResidentDetails(residentId)
      .then((response) => {
        setResidentDetails(response.data);
        setResidentDraft(null);
      })
      .catch((err) => {
        setResidentDetails(null);
        setResidentDraft(null);
        setResidentDetailsError(err?.message || 'Unable to load resident details');
      });
  }, [request.residents]);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <ActionBar request={request} setRequest={setRequest} isEditing={hasUnsavedChanges} allowEdit={allowEdit} />
        <ResidentHeaderSection
          residents={request.residents}
          requestStatusId={request.requestStatusId}
          facilityName={request.facilityName}
          isRecurring={request.isRecurring}
          isChildRequest={request.isChildRequest}
          parentRequestId={request.parentRequestId}
          onOpenParentRequest={() => {
            navigate(`/dashboard/transports/request-details/${request.parentRequestId}`);
          }}
          residentDetails={residentDetails}
        />
        <Card sx={{ padding: 2, borderRadius: 1, mt: 1, mb: 2, width: '100%', py: 3, px: { xs: 3, sm: 6 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 3,
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flex: 1, minWidth: 0, '& .MuiTabs-indicator': { display: 'none' } }}
            >
              <Tab
                value={transportDetailsTab}
                label={
                  <Tooltip
                    title="Please save or cancel your edits"
                    placement="top"
                    arrow
                    disableHoverListener={!(hasUnsavedChanges && selectedTab !== transportDetailsTab)}
                  >
                    <span>Transport Details</span>
                  </Tooltip>
                }
                sx={tabStyle}
                disabled={hasUnsavedChanges && !transportDetailsTabIsActive}
              />
              <Tab
                value={residentDetailsTab}
                label={
                  <Tooltip
                    title="Please save or cancel your edits"
                    placement="top"
                    arrow
                    disableHoverListener={!(hasUnsavedChanges && selectedTab !== residentDetailsTab)}
                  >
                    <span>Resident Details</span>
                  </Tooltip>
                }
                sx={tabStyle}
                disabled={hasUnsavedChanges && !residentDetailsTabIsActive}
              />
              <Tab
                value={additionalDetailsTab}
                label={
                  <Tooltip
                    title="Please save or cancel your edits"
                    placement="top"
                    arrow
                    disableHoverListener={!(hasUnsavedChanges && selectedTab !== additionalDetailsTab)}
                  >
                    <span>Additional Details</span>
                  </Tooltip>
                }
                sx={tabStyle}
                disabled={hasUnsavedChanges && selectedTab !== additionalDetailsTab}
              />
            </Tabs>
            {canEdit && selectedTab !== additionalDetailsTab && hasUnsavedChanges && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '14px',
                    color: 'grey.800',
                    borderColor: 'grey.300',
                    '&:hover': { borderColor: 'grey.300', backgroundColor: 'action.hover' },
                  }}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <AwaitButton
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={residentDetailsTabIsActive ? submitLoading : !isFormValid || submitLoading}
                  sx={{ fontSize: '14px' }}
                  onClick={() => {
                    if (residentDetailsTabIsActive) {
                      handleResidentSubmit();
                    } else if (wasRecurring.current && transportDetailsTabIsActive) {
                      setShowConfirmationPopup(true);
                    } else {
                      handleSubmit();
                    }
                  }}
                >
                  Save Changes
                </AwaitButton>
                {formState.IsRecurring &&
                  transportDetailsTabIsActive &&
                  (recurrenceRulesChanged ? (
                    <ConfirmDialogModal
                      title={'Recurring Rule Change'}
                      content={
                        <>
                          <Typography gutterBottom>
                            You have changed the recurrence rules for this request. This will update all future events
                            in the series. Changes to recurrence rules cannot be applied to only a single event.
                          </Typography>
                          <Typography color="warning.main">
                            This action will update the recurrence pattern for all future events. Past events will not
                            be affected.
                          </Typography>
                        </>
                      }
                      action={() => handleSubmit(false)}
                      color={'primary'}
                      actionButton={'Update All Future Events'}
                      setOpenConfirm={() => setShowConfirmationPopup(null)}
                      openConfirm={!!showConfirmationPopup}
                    />
                  ) : (
                    <ConfirmDialogModal
                      title={'Confirmation'}
                      content={'You are changing a recurring request.  Apply changes to:'}
                      action={handleSubmit}
                      color={'primary'}
                      actionButton={'All Future Events'}
                      setOpenConfirm={() => setShowConfirmationPopup(null)}
                      openConfirm={!!showConfirmationPopup}
                      extraAction={{
                        label: 'Only This Event',
                        onClick: () => handleSubmit(true),
                        color: 'primary',
                        variant: 'contained',
                      }}
                    />
                  ))}
              </Box>
            )}
          </Box>

          <Alert
            open={showSaveSuccess}
            severity="success"
            onClose={() => {
              setShowSaveSuccess(false);
            }}
            sx={{ mb: 2 }}
            message={`Changes saved successfully for this ${
              transportDetailsTabIsActive ? `event${updatedFutureEvents ? ' and all future events' : ''}` : 'resident'
            }`}
          />
          <Alert
            open={Boolean(submitError)}
            severity="error"
            onClose={() => setSubmitError('')}
            sx={{ mb: 2 }}
            message={submitError}
          />

          <Tooltip title={canEdit ? '' : readOnlyMessage} placement="top" arrow followCursor>
            <Box>
              {transportDetailsTabIsActive && (
                <TransportDetailsTab
                  request={request}
                  formState={formState}
                  setFields={setFields}
                  editable={canEdit}
                  setIsAllowedPickupDate={setIsAllowedPickupDate}
                  setIsAllowedAppointmentDate={setIsAllowedAppointmentDate}
                  wasRecurring={wasRecurring.current}
                />
              )}
              {residentDetailsTabIsActive && (
                <ResidentDetailsTab
                  request={request}
                  residentDetails={residentDraft ?? residentDetails}
                  residentDetailsError={residentDetailsError}
                  editable={canEdit}
                  onResidentFieldChange={handleResidentFieldChange}
                />
              )}
            </Box>
          </Tooltip>
          {selectedTab === additionalDetailsTab && <AdditionalDetailsTab request={request} />}
        </Card>
      </Grid>
    </Grid>
  );
}
